/**
 * Hardware Provider detail — one manufacturer: its boards and their prices,
 * and the devices built on those boards with the software provider each is
 * connected to. Marketplace stage 1 (docs/planning/MARKETPLACE_PLAN.md).
 *
 * Super admins reach it from the Hardware Providers list. A manufacturer
 * admin lands here (their own row) — it is their whole dashboard, and the API
 * scopes every call to their manufacturer.
 */
import { useRef, useState } from 'react';
import { useParams, Link, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { IconX } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin, isManufacturerAdmin } from '../services/users';
import { getManufacturer, getManufacturerDevices, updateManufacturer, uploadLogo, deleteLogo, logoPreviewUrl, LOGO_ACCEPT, getPayoutStatus, startPayoutOnboarding, getPayoutLoginLink, getServiceShares } from '../services/manufacturers';
import { AuthImage } from '../components/AuthImage';
import type { Manufacturer, ManufacturerInput } from '../services/manufacturers';
import { getDeviceBoards, boardLabel, formatMoney, createDeviceBoard } from '../services/deviceBoards';
import type { DeviceBoard, BoardPrice } from '../services/deviceBoards';
import { getListing, createVariant, updateVariant, uploadAsset, VARIANT_STATUS_LABELS, IMAGE_ACCEPT, DOCUMENT_ACCEPT } from '../services/listings';
import type { BoardVariant, VariantStatus } from '../services/listings';
import { setVariantPrice } from '../services/deviceBoards';
import { getMarketplaceSettings, formatBps } from '../services/platformSettings';
import { BoardPriceModal, formatWhen } from '../components/BoardPriceModal';
import { SegmentedTabs } from '../components/SegmentedTabs';
import { ProviderDeviceFleet } from '../components/ProviderDeviceFleet';
import { ProviderOrders } from '../components/ProviderOrders';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  Button,
  TableContainer,
  StyledTable,
  Th,
  Td,
  StatusChip,
  AlertBanner,
  LoadingText,
  EmptyState,
  MutedText,
  Caption,
  Row,
  Stack,
  StatsGrid,
  StatCard,
  DetailCard,
  SectionTitle,
  Select,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  TextArea,
  HelperText,
  ErrorAlert,
  Checkbox,
  H2Title,
} from '../components/styled';

const BackLink = styled(Link)`
  color: ${({ theme }) => theme.font.color.secondary};
  text-decoration: none;
  font-size: ${({ theme }) => theme.font.size.sm};

  &:hover {
    color: ${({ theme }) => theme.accent.primary};
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 ${({ theme }) => theme.spacing(3)};
`;

const PriceLine = styled.div`
  white-space: nowrap;
`;

// SectionTitle has no top margin and DetailCard/StatsGrid no bottom margin
// beyond the grid's, so a title following a card sits flush against it.
// Same fix CompanyDetail uses.
const SpacedSectionTitle = styled(SectionTitle)`
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin: ${({ theme }) => theme.spacing(6)} 0 ${({ theme }) => theme.spacing(3)};

  h2 { margin: 0; }
`;

const ProviderLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};

  &:hover { text-decoration: underline; }
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  .well {
    width: 120px;
    height: 72px;
    border-radius: ${({ theme }) => theme.border.radius.md};
    border: 1px solid ${({ theme }) => theme.border.color.light};
    background: ${({ theme }) => theme.background.secondary};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
  }
  .well img { max-width: 100%; max-height: 100%; object-fit: contain; }
  input[type='file'] { display: none; }
`;

const DetailGrid = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: ${({ theme }) => theme.spacing(1.5, 4)};
  margin: 0;

  dt {
    color: ${({ theme }) => theme.font.color.tertiary};
    font-size: ${({ theme }) => theme.font.size.sm};
  }
  dd { margin: 0; }
`;

type Tab = 'overview' | 'boards' | 'devices' | 'orders' | 'payouts';

// A variant row carrying the price in force (its own, or the board's family
// default flagged inherited) — shape served by GET /device-boards/:id/listing.
type PricedVariant = BoardVariant & { current_price?: (BoardPrice & { inherited?: boolean }) | null };

/**
 * One board family in the Boards & pricing tab: header row (identity, listing
 * state, actions) plus a row per SKU with its price. Pricing is per SKU (user
 * decision Sept 2026); the board price is only the family default new SKUs
 * inherit until priced.
 */
function BoardPricingGroup({ board, onPrice, onAddSku, onListing }: {
  board: DeviceBoard;
  onPrice: (variant: PricedVariant) => void;
  onAddSku: () => void;
  onListing: () => void;
}) {
  const { data } = useQuery({
    queryKey: ['board-variants-priced', board.id],
    queryFn: () => getListing(board.id),
  });
  const variants = (data?.variants ?? []) as PricedVariant[];

  return (
    <div style={{ marginBottom: 28 }}>
      <SectionHeader>
        <Row gap={2} align="center">
          <strong>{boardLabel(board)}</strong>
          <Caption>{board.manufacturer} · {board.board} · {board.device_count} device{board.device_count === 1 ? '' : 's'}</Caption>
          <StatusChip variant={board.listing_published ? 'positive' : 'neutral'}>
            {board.listing_published ? 'published' : 'draft'}
          </StatusChip>
        </Row>
        <Row gap={2}>
          <Button size="sm" variant="secondary" onClick={onAddSku}>Add SKU</Button>
          <Button size="sm" variant="secondary" onClick={onListing}>
            {board.listing_published ? 'Edit listing' : 'Write listing'}
          </Button>
        </Row>
      </SectionHeader>
      {variants.length === 0 ? (
        <EmptyState>
          <p>No SKUs yet.</p>
          <MutedText>Add a SKU (e.g. {board.board}-01) — prices belong to SKUs, not the board.</MutedText>
        </EmptyState>
      ) : (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <Th>SKU</Th>
                <Th>Product</Th>
                <Th>Hardware price</Th>
                <Th>Monthly fee / device</Th>
                <Th>In force since</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {variants.map((v) => (
                <tr key={v.id}>
                  <Td><strong>{v.sku}</strong></Td>
                  <Td>{v.name}</Td>
                  {v.current_price ? (
                    <>
                      <Td>
                        <PriceLine>{formatMoney(v.current_price.hardware_price_cents, v.current_price.currency)}</PriceLine>
                        {v.current_price.inherited && <> <StatusChip variant="neutral">inherited</StatusChip></>}
                      </Td>
                      <Td><PriceLine>{formatMoney(v.current_price.monthly_fee_cents, v.current_price.currency)}</PriceLine></Td>
                      <Td><Caption>{formatWhen(v.current_price.effective_from)}</Caption></Td>
                    </>
                  ) : (
                    <Td colSpan={3}><StatusChip variant="warning">not priced</StatusChip></Td>
                  )}
                  <Td><Caption>{v.status}</Caption></Td>
                  <Td>
                    <Button
                      size="sm"
                      variant={v.current_price && !v.current_price.inherited ? 'secondary' : 'primary'}
                      onClick={() => onPrice(v)}
                    >
                      {v.current_price && !v.current_price.inherited ? 'Change price' : 'Set price'}
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableContainer>
      )}
    </div>
  );
}

function statusVariant(status: string): 'positive' | 'critical' | 'neutral' {
  if (status === 'online') return 'positive';
  if (status === 'offline') return 'critical';
  return 'neutral';
}

/**
 * Full product-SKU creation (user-decided field set): photo, headline,
 * status, part number, name, price, recurring cost yes/no, documents.
 * Creates the variant, then uploads the photo (set as hero) and documents,
 * then prices it — partial failures are reported, never silent.
 */
function AddSkuModal({ board, onClose }: { board: DeviceBoard; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    sku: '', name: '', headline: '', status: 'available' as VariantStatus,
    price: '', recurring: false, monthly: '',
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [docs, setDocs] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const pickPhoto = (f: File | null) => {
    setPhoto(f);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(f ? URL.createObjectURL(f) : null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErrors([]);
    const problems: string[] = [];

    // 1. The product itself — everything else hangs off it.
    let variantId: number;
    try {
      const variant = await createVariant(board.id, {
        name: form.name.trim(),
        sku: form.sku.trim(),
        headline: form.headline.trim() || null,
        status: form.status,
      });
      variantId = variant.id;
    } catch (err: any) {
      const body = err.response?.data;
      setErrors([body?.details?.join?.('; ') || body?.error || 'Failed to create the SKU']);
      setBusy(false);
      return;
    }

    // 2. Photo → uploaded against the SKU, set as its hero image.
    if (photo) {
      try {
        const asset = await uploadAsset(board.id, photo, { variantId });
        await updateVariant(board.id, variantId, { hero_asset_id: asset.id });
      } catch (err: any) {
        problems.push(`photo: ${err.response?.data?.error || 'upload failed'}`);
      }
    }

    // 3. Documents (datasheets, install guides).
    for (const doc of docs) {
      try {
        await uploadAsset(board.id, doc, { variantId });
      } catch (err: any) {
        problems.push(`${doc.name}: ${err.response?.data?.error || 'upload failed'}`);
      }
    }

    // 4. Price — hardware plus the recurring monthly fee when enabled.
    if (form.price.trim() !== '') {
      try {
        await setVariantPrice(board.id, variantId, {
          currency: 'USD',
          hardware_price_cents: Math.round(Number(form.price) * 100),
          monthly_fee_cents: form.recurring ? Math.round(Number(form.monthly || '0') * 100) : 0,
        });
      } catch (err: any) {
        const body = err.response?.data;
        problems.push(`price: ${body?.details?.join?.('; ') || body?.error || 'failed to set'}`);
      }
    }

    queryClient.invalidateQueries({ queryKey: ['board-variants-priced', board.id] });
    queryClient.invalidateQueries({ queryKey: ['device-boards'] });
    if (problems.length) {
      // The SKU exists — surface exactly what didn't make it.
      setErrors([`${form.sku.trim()} was created, but:`, ...problems]);
      setBusy(false);
    } else {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <H2Title>Add product to {boardLabel(board)}</H2Title>
          <ModalCloseButton onClick={onClose} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
        </ModalHeader>
        <form onSubmit={submit}>
          <ModalBody>
            {errors.length > 0 && (
              <ErrorAlert as="div">{errors.map((line, i) => <div key={i}>{line}</div>)}</ErrorAlert>
            )}

            <FormGroup>
              <Label>Photo of the unit</Label>
              {photoPreview && (
                <img src={photoPreview} alt="Product preview" style={{ maxWidth: 160, maxHeight: 120, objectFit: 'contain', display: 'block', marginBottom: 8 }} />
              )}
              <Input
                type="file"
                accept={IMAGE_ACCEPT}
                onChange={(e) => pickPhoto(e.target.files?.[0] || null)}
              />
              <HelperText>Shown as the product's hero image on the public listing.</HelperText>
            </FormGroup>

            <FieldGrid>
              <FormGroup>
                <Label>Part number</Label>
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder={`${board.board}-01`} required />
              </FormGroup>
              <FormGroup>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Surface mount, black" required />
              </FormGroup>
            </FieldGrid>

            <FormGroup>
              <Label>Headline</Label>
              <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="One-line pitch shown under the name" maxLength={200} />
            </FormGroup>

            <FieldGrid>
              <FormGroup>
                <Label>Status</Label>
                <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as VariantStatus })}>
                  {Object.entries(VARIANT_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Price (per unit, USD)</Label>
                <Input type="number" min={0} step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="249.00" />
                <HelperText>Leave blank to inherit the board default for now.</HelperText>
              </FormGroup>
            </FieldGrid>

            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.recurring}
                  onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
                />
                Recurring cost
              </Label>
              {form.recurring && (
                <>
                  <Input type="number" min={0} step="0.01" value={form.monthly} onChange={(e) => setForm({ ...form, monthly: e.target.value })} placeholder="5.00" required />
                  <HelperText>Monthly service fee per deployed device.</HelperText>
                </>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Documents</Label>
              <Input
                type="file"
                accept={DOCUMENT_ACCEPT}
                multiple
                onChange={(e) => setDocs(Array.from(e.target.files || []))}
              />
              {docs.length > 0 && <Caption>{docs.map((d) => d.name).join(', ')}</Caption>}
              <HelperText>Datasheets, install guides (PDF). Shown on the product page.</HelperText>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" type="button" onClick={onClose}>{errors.length > 1 ? 'Close' : 'Cancel'}</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Creating…' : 'Add product'}</Button>
          </ModalFooter>
        </form>
      </ModalCard>
    </ModalOverlay>
  );
}

/** Seller-facing board-type creation; capabilities are refined later in the catalog. */
function AddBoardModal({ manufacturerId, onClose }: { manufacturerId: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ board: '', display_name: '', num_doors: '1', power_type: 'battery' });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => createDeviceBoard({
      board: form.board.trim(),
      display_name: form.display_name.trim() || null,
      default_num_doors: Math.max(1, parseInt(form.num_doors, 10) || 1),
      default_power_type: form.power_type as 'battery' | 'plugin',
      manufacturer_id: manufacturerId,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-boards'] });
      queryClient.invalidateQueries({ queryKey: ['manufacturer'] });
      onClose();
    },
    onError: (err: any) => {
      const body = err.response?.data;
      setError(body?.details?.join?.('; ') || body?.error || 'Failed to add board');
    },
  });

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <H2Title>Add board type</H2Title>
          <ModalCloseButton onClick={onClose} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
        </ModalHeader>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
          <ModalBody>
            {error && <ErrorAlert>{error}</ErrorAlert>}
            <FieldGrid>
              <FormGroup>
                <Label>Board (model family)</Label>
                <Input value={form.board} onChange={(e) => setForm({ ...form, board: e.target.value })} placeholder="SB" required />
                <HelperText>The platform family — SKUs like {form.board.trim() || 'SB'}-01 hang off it.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label>Display name</Label>
                <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Optional" />
              </FormGroup>
            </FieldGrid>
            <FieldGrid>
              <FormGroup>
                <Label>Doors per device</Label>
                <Input type="number" min={1} max={32} value={form.num_doors} onChange={(e) => setForm({ ...form, num_doors: e.target.value })} required />
              </FormGroup>
              <FormGroup>
                <Label>Power</Label>
                <Select value={form.power_type} onChange={(e) => setForm({ ...form, power_type: e.target.value })}>
                  <option value="battery">Battery</option>
                  <option value="plugin">Plug-in</option>
                </Select>
              </FormGroup>
            </FieldGrid>
            <HelperText>
              Capability declarations (locks, readers, card formats) are refined with
              the Simkura team in the board catalog before devices ship on it.
            </HelperText>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>{mutation.isPending ? 'Adding…' : 'Add board'}</Button>
          </ModalFooter>
        </form>
      </ModalCard>
    </ModalOverlay>
  );
}

/* ── edit modal (contact details; identity + active are super-admin only) ── */

interface EditForm {
  name: string;
  slug: string;
  support_email: string;
  website: string;
  active: boolean;
  notes: string;
  tagline: string;
  about: string;
  offers_terms: boolean;
}

function HardwareProviderDetail() {
  const { id } = useParams();
  const manufacturerId = Number(id);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestedTab = (searchParams.get('tab') as Tab) || 'overview';
  const [pricingTarget, setPricingTarget] = useState<{ board: DeviceBoard; variant: PricedVariant } | null>(null);
  const [addingSkuBoard, setAddingSkuBoard] = useState<DeviceBoard | null>(null);
  const [addingBoard, setAddingBoard] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  const userIsSuperAdmin = isSuperAdmin(user);
  const userIsManufacturerAdmin = isManufacturerAdmin(user);
  // Sellers reach their fleet from the left nav (/hardware-devices); only
  // super admins keep the Devices tab here. Stale ?tab=devices deep-links
  // for sellers fall back to Overview.
  const activeTab: Tab = requestedTab === 'devices' && !userIsSuperAdmin ? 'overview' : requestedTab;

  // Company roles have no business here; a seller only their own row.
  if (!userIsSuperAdmin && !userIsManufacturerAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  if (userIsManufacturerAdmin && user?.manufacturer_id != null && user.manufacturer_id !== manufacturerId) {
    return <Navigate to={`/hardware-providers/${user.manufacturer_id}`} replace />;
  }

  const { data: manufacturer, isLoading, isError } = useQuery({
    queryKey: ['manufacturer', manufacturerId],
    queryFn: () => getManufacturer(manufacturerId),
    enabled: Number.isFinite(manufacturerId),
  });
  const { data: boards = [] } = useQuery({
    queryKey: ['device-boards', manufacturerId],
    queryFn: () => getDeviceBoards({ manufacturerId }),
    enabled: Number.isFinite(manufacturerId),
  });
  // Software-provider breakdown for the Overview table (the fleet itself
  // lives in ProviderDeviceFleet — the seller's Devices page / the
  // super-admin Devices tab).
  const { data: breakdownData } = useQuery({
    queryKey: ['manufacturer-devices-breakdown', manufacturerId],
    queryFn: () => getManufacturerDevices(manufacturerId, { limit: 1 }),
    enabled: Number.isFinite(manufacturerId),
  });
  const { data: settings } = useQuery({
    queryKey: ['marketplace-settings'],
    queryFn: getMarketplaceSettings,
  });

  // Payout onboarding status; refetched on tab focus so returning from
  // Stripe onboarding (?tab=payouts&return=1) shows the fresh state.
  const { data: payouts, isLoading: payoutsLoading } = useQuery({
    queryKey: ['manufacturer-payouts', manufacturerId],
    queryFn: () => getPayoutStatus(manufacturerId),
    enabled: Number.isFinite(manufacturerId) && activeTab === 'payouts',
    refetchOnWindowFocus: true,
  });

  // Recurring service revenue — the provider's monthly share of buyers'
  // service statements (shown on the Payouts tab).
  const { data: sharesData } = useQuery({
    queryKey: ['manufacturer-service-shares', manufacturerId],
    queryFn: () => getServiceShares(manufacturerId),
    enabled: Number.isFinite(manufacturerId) && activeTab === 'payouts',
  });
  const serviceShares = sharesData?.shares ?? [];

  const [payoutError, setPayoutError] = useState<string | null>(null);
  const onboardMutation = useMutation({
    mutationFn: () => startPayoutOnboarding(manufacturerId),
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (err: any) => setPayoutError(err.response?.data?.message || err.response?.data?.error || 'Failed to start onboarding'),
  });
  const loginLinkMutation = useMutation({
    mutationFn: () => getPayoutLoginLink(manufacturerId),
    onSuccess: ({ url }) => { window.open(url, '_blank', 'noreferrer'); },
    onError: (err: any) => setPayoutError(err.response?.data?.message || err.response?.data?.error || 'Failed to open the Stripe dashboard'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: ManufacturerInput) => updateManufacturer(manufacturerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturer', manufacturerId] });
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      queryClient.invalidateQueries({ queryKey: ['device-boards'] });
      setEditing(false);
    },
    onError: (err: any) => {
      const body = err.response?.data;
      setEditError(body?.details?.join?.('; ') || body?.error || 'Failed to save');
    },
  });

  const logoMutation = useMutation({
    mutationFn: (file: File | null) => (file ? uploadLogo(manufacturerId, file) : deleteLogo(manufacturerId)),
    onSuccess: () => {
      setLogoError(null);
      queryClient.invalidateQueries({ queryKey: ['manufacturer', manufacturerId] });
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
    },
    onError: (err: any) => setLogoError(err.response?.data?.error || 'Failed to update logo'),
  });

  const setTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const openEdit = (m: Manufacturer) => {
    setEditForm({
      name: m.name,
      slug: m.slug,
      support_email: m.support_email || '',
      website: m.website || '',
      active: m.active,
      notes: m.notes || '',
      tagline: m.tagline || '',
      about: m.about || '',
      offers_terms: m.offers_terms === true,
    });
    setEditError(null);
    setEditing(true);
  };

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm) return;
    // A manufacturer admin may only change contact fields; the API refuses the rest.
    const data: ManufacturerInput = userIsSuperAdmin
      ? {
          name: editForm.name.trim(),
          ...(editForm.slug.trim() ? { slug: editForm.slug.trim() } : {}),
          support_email: editForm.support_email.trim() || null,
          website: editForm.website.trim() || null,
          active: editForm.active,
          notes: editForm.notes.trim() || null,
          tagline: editForm.tagline.trim() || null,
          about: editForm.about.trim() || null,
          offers_terms: editForm.offers_terms,
        }
      : {
          support_email: editForm.support_email.trim() || null,
          website: editForm.website.trim() || null,
          notes: editForm.notes.trim() || null,
          tagline: editForm.tagline.trim() || null,
          about: editForm.about.trim() || null,
          offers_terms: editForm.offers_terms,
        };
    updateMutation.mutate(data);
  };

  if (isLoading) return <PageContainer><LoadingText>Loading hardware provider...</LoadingText></PageContainer>;
  if (isError || !manufacturer) {
    return (
      <PageContainer>
        <EmptyState>
          <p>Hardware provider not found.</p>
          {userIsSuperAdmin && <p><Link to="/hardware-providers">Back to Hardware Providers</Link></p>}
        </EmptyState>
      </PageContainer>
    );
  }

  const providers = breakdownData?.software_providers ?? [];
  const unpriced = boards.filter((b) => !b.current_price).length;
  const cutLabel = settings ? formatBps(settings.platform_fee_bps) : null;

  return (
    <PageContainer>
      <PageHeader>
        <div>
          {userIsSuperAdmin && <BackLink to="/hardware-providers">← Hardware Providers</BackLink>}
          <Row gap={2} align="center">
            <PageTitle>{manufacturer.name}</PageTitle>
            {!manufacturer.active && <StatusChip variant="neutral">inactive</StatusChip>}
          </Row>
          <Caption>{manufacturer.slug}</Caption>
        </div>
        <Button variant="secondary" onClick={() => openEdit(manufacturer)}>Edit details</Button>
      </PageHeader>

      {!manufacturer.active && (
        <AlertBanner variant="warning">
          {userIsSuperAdmin
            ? 'This provider is inactive: nothing it publishes appears in the public catalogue or store. Use Edit details → Active to approve it.'
            : 'Your provider profile is pending approval. You can prepare boards and listings now; they appear in the public catalogue once Simkura activates your profile.'}
        </AlertBanner>
      )}

      <SegmentedTabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'boards', label: `Boards & pricing (${boards.length})` },
          ...(userIsSuperAdmin
            ? [
                { id: 'devices', label: `Devices (${manufacturer.device_count})` },
                { id: 'orders', label: 'Orders' },
              ]
            : []),
          { id: 'payouts', label: 'Payouts' },
        ]}
        activeId={activeTab}
        onChange={setTab}
      />

      {activeTab === 'overview' && (
        <>
          <StatsGrid>
            <StatCard>
              <h3>Boards</h3>
              <p className="stat-value">{manufacturer.board_count}</p>
              <small>{unpriced > 0 ? `${unpriced} not priced` : 'all priced'}</small>
            </StatCard>
            <StatCard>
              <h3>Deployed devices</h3>
              <p className="stat-value">{manufacturer.deployed_device_count}</p>
              <small>of {manufacturer.device_count} on the platform</small>
            </StatCard>
            <StatCard>
              <h3>Software providers</h3>
              <p className="stat-value">{manufacturer.software_provider_count}</p>
              <small>with deployed devices</small>
            </StatCard>
            <StatCard>
              <h3>Platform cut</h3>
              <p className="stat-value">{cutLabel ?? '…'}</p>
              <small>taken out of your prices</small>
            </StatCard>
          </StatsGrid>

          <SpacedSectionTitle>Details</SpacedSectionTitle>
          <DetailCard>
            <LogoRow>
              <div className="well">
                {manufacturer.has_logo
                  ? <AuthImage src={logoPreviewUrl(manufacturer.id)} version={manufacturer.logo_updated_at} alt={`${manufacturer.name} logo`} fallback={<Caption>logo</Caption>} />
                  : <Caption>no logo</Caption>}
              </div>
              <Stack gap={1}>
                <Row gap={2}>
                  <Button size="sm" variant="secondary" disabled={logoMutation.isPending} onClick={() => logoInput.current?.click()}>
                    {manufacturer.has_logo ? 'Replace logo' : 'Upload logo'}
                  </Button>
                  {manufacturer.has_logo && (
                    <Button size="sm" variant="danger" disabled={logoMutation.isPending} onClick={() => logoMutation.mutate(null)}>Remove</Button>
                  )}
                  <input
                    ref={logoInput}
                    type="file"
                    accept={LOGO_ACCEPT}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) logoMutation.mutate(f); e.target.value = ''; }}
                  />
                </Row>
                <Caption>Shown on the public catalog. PNG, JPG or WebP up to 1 MB; wide logos on a transparent or white background look best.</Caption>
                {logoError && <ErrorAlert>{logoError}</ErrorAlert>}
              </Stack>
            </LogoRow>
            <DetailGrid>
              <dt>Support email</dt>
              <dd>{manufacturer.support_email || <MutedText>—</MutedText>}</dd>
              <dt>Website</dt>
              <dd>{manufacturer.website ? <a href={manufacturer.website} target="_blank" rel="noreferrer">{manufacturer.website}</a> : <MutedText>—</MutedText>}</dd>
              <dt>Admin accounts</dt>
              <dd>{manufacturer.user_count}</dd>
              <dt>Product overview</dt>
              <dd>{manufacturer.tagline || <MutedText>— shown on the public Hardware providers page once set</MutedText>}</dd>
              {manufacturer.notes && (
                <>
                  <dt>Notes</dt>
                  <dd>{manufacturer.notes}</dd>
                </>
              )}
            </DetailGrid>
          </DetailCard>

          <SpacedSectionTitle>Software providers using {manufacturer.name} hardware</SpacedSectionTitle>
          {providers.length === 0 ? (
            <EmptyState><p>No devices on {manufacturer.name} boards are assigned to a software provider yet.</p></EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Software provider</Th>
                    <Th>Deployed</Th>
                    <Th>Total devices</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {providers.map((p) => (
                    <tr key={String(p.company_id)}>
                      <Td>
                        {userIsSuperAdmin
                          ? <ProviderLink to={`/software-providers/${p.company_id}`}>{p.company_name}</ProviderLink>
                          : <strong>{p.company_name}</strong>}
                      </Td>
                      <Td>{p.deployed_count}</Td>
                      <Td>{p.device_count}</Td>
                      <Td>
                        <Button size="sm" variant="secondary" onClick={() => (userIsSuperAdmin ? setTab('devices') : navigate('/hardware-devices'))}>
                          View devices
                        </Button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}

      {activeTab === 'boards' && (
        <>
          <SectionHeader>
            <MutedText>
              Pricing is per SKU — the board is the platform family ({boards[0]?.board || 'SB'}),
              each product ({boards[0]?.board || 'SB'}-01, -02…) carries its own hardware price and
              monthly per-device fee. A SKU without a price of its own inherits the board default.
              {cutLabel && ` The platform cut of ${cutLabel} comes out of these amounts.`}
              {userIsSuperAdmin && <> Capabilities and features are edited in the <Link to={`/admin/boards?manufacturerId=${manufacturer.id}`}>board catalog</Link>.</>}
            </MutedText>
            <Button size="sm" onClick={() => setAddingBoard(true)}>Add board</Button>
          </SectionHeader>
          {boards.length === 0 ? (
            <EmptyState><p>No boards belong to {manufacturer.name} yet — add your first board type.</p></EmptyState>
          ) : (
            boards.map((b) => (
              <BoardPricingGroup
                key={b.id}
                board={b}
                onPrice={(variant) => setPricingTarget({ board: b, variant })}
                onAddSku={() => setAddingSkuBoard(b)}
                onListing={() => navigate(`/hardware-providers/${manufacturer.id}/boards/${b.id}/listing`)}
              />
            ))
          )}
        </>
      )}

      {userIsSuperAdmin && activeTab === 'devices' && (
        <ProviderDeviceFleet
          manufacturerId={manufacturerId}
          manufacturerName={manufacturer.name}
          linkThrough
        />
      )}

      {userIsSuperAdmin && activeTab === 'orders' && (
        <ProviderOrders manufacturerId={manufacturerId} linkThrough />
      )}

      {activeTab === 'payouts' && (
        <>
          <SectionHeader>
            <MutedText>
              Where your hardware revenue and monthly service share are paid.
              Powered by Stripe — Simkura never sees your bank details.
            </MutedText>
          </SectionHeader>

          {payoutError && <ErrorAlert>{payoutError}</ErrorAlert>}

          {payoutsLoading && <LoadingText>Checking payout status...</LoadingText>}

          {payouts && !payouts.configured && (
            <EmptyState>
              <p>Payouts aren't enabled on this environment yet.</p>
              <MutedText>Once Stripe is configured, you'll connect your payout account here.</MutedText>
            </EmptyState>
          )}

          {payouts?.configured && !payouts.onboarded && (
            <EmptyState>
              <p>No payout account connected.</p>
              <MutedText>Set up secure payouts through Stripe — it takes a few minutes.</MutedText>
              <div style={{ marginTop: 16 }}>
                <Button onClick={() => onboardMutation.mutate()} disabled={onboardMutation.isPending}>
                  {onboardMutation.isPending ? 'Redirecting…' : 'Set up payouts'}
                </Button>
              </div>
            </EmptyState>
          )}

          {payouts?.configured && payouts.onboarded && (
            <Stack gap={3}>
              <Row gap={2} align="center">
                <StatusChip variant={payouts.payoutsEnabled ? 'positive' : 'warning'}>
                  {payouts.payoutsEnabled ? 'Payouts active' : payouts.detailsSubmitted ? 'Under Stripe review' : 'Onboarding incomplete'}
                </StatusChip>
                {payouts.accountId && <Caption>{payouts.accountId}</Caption>}
              </Row>
              <Row gap={2}>
                {!payouts.detailsSubmitted && (
                  <Button onClick={() => onboardMutation.mutate()} disabled={onboardMutation.isPending}>
                    {onboardMutation.isPending ? 'Redirecting…' : 'Resume onboarding'}
                  </Button>
                )}
                {payouts.detailsSubmitted && (
                  <Button variant="secondary" onClick={() => loginLinkMutation.mutate()} disabled={loginLinkMutation.isPending}>
                    {loginLinkMutation.isPending ? 'Opening…' : 'Open Stripe dashboard'}
                  </Button>
                )}
              </Row>
              <MutedText>
                {payouts.payoutsEnabled
                  ? 'Bank account, payout schedule and balance are managed in the Stripe dashboard.'
                  : payouts.detailsSubmitted
                    ? 'Stripe is reviewing your details — payouts activate automatically when review completes.'
                    : 'Finish the Stripe form to activate payouts.'}
              </MutedText>
            </Stack>
          )}

          <SpacedSectionTitle>Service revenue</SpacedSectionTitle>
          {serviceShares.length === 0 ? (
            <EmptyState>
              <p>No service revenue yet.</p>
              <MutedText>
                Each month, every deployed device sold through the marketplace earns
                its monthly service fee — your share (after the platform cut) shows
                here and transfers to your payout account.
              </MutedText>
            </EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Period</Th>
                    <Th>Buyer</Th>
                    <Th>Gross</Th>
                    <Th>Platform cut</Th>
                    <Th>Your net</Th>
                    <Th>Statement</Th>
                    <Th>Transfer</Th>
                  </tr>
                </thead>
                <tbody>
                  {serviceShares.map((sh) => (
                    <tr key={sh.id}>
                      <Td>{new Date(sh.period_start).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })}</Td>
                      <Td>{sh.company_name}</Td>
                      <Td>{formatMoney(sh.gross_cents, sh.currency)}</Td>
                      <Td><MutedText>−{formatMoney(sh.platform_fee_cents, sh.currency)}</MutedText></Td>
                      <Td><strong>{formatMoney(sh.net_cents, sh.currency)}</strong></Td>
                      <Td><StatusChip variant={sh.statement_status === 'paid' ? 'green' : 'warning'}>{sh.statement_status}</StatusChip></Td>
                      <Td>
                        <StatusChip variant={
                          sh.transfer_status === 'transferred' ? 'positive'
                            : sh.transfer_status === 'failed' ? 'critical' : 'neutral'
                        }>
                          {sh.transfer_status.replace('_', ' ')}
                        </StatusChip>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}

      {pricingTarget && (
        <BoardPriceModal
          board={pricingTarget.board}
          variant={pricingTarget.variant}
          onClose={() => setPricingTarget(null)}
        />
      )}

      {addingSkuBoard && (
        <AddSkuModal board={addingSkuBoard} onClose={() => setAddingSkuBoard(null)} />
      )}

      {addingBoard && (
        <AddBoardModal manufacturerId={manufacturer.id} onClose={() => setAddingBoard(false)} />
      )}

      {editing && editForm && (
        <ModalOverlay onClick={() => setEditing(false)}>
          <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Edit {manufacturer.name}</H2Title>
              <ModalCloseButton onClick={() => setEditing(false)} aria-label="Close">
                <IconX size={18} stroke={2} />
              </ModalCloseButton>
            </ModalHeader>
            <form onSubmit={submitEdit}>
              <ModalBody>
                {editError && <ErrorAlert>{editError}</ErrorAlert>}
                {userIsSuperAdmin && (
                  <FieldGrid>
                    <FormGroup>
                      <Label>Name</Label>
                      <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
                      <HelperText>Shown as <code>device.manufacturer</code> on every board it owns.</HelperText>
                    </FormGroup>
                    <FormGroup>
                      <Label>Slug</Label>
                      <Input value={editForm.slug} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })} pattern="[a-z0-9]+(-[a-z0-9]+)*" />
                    </FormGroup>
                  </FieldGrid>
                )}
                <FormGroup>
                  <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={editForm.offers_terms}
                      onChange={(e) => setEditForm({ ...editForm, offers_terms: e.target.checked })}
                    />
                    Offer terms (net billing) to buyers at checkout
                  </Label>
                  <HelperText>
                    Terms orders arrive pending — you accept or decline each one, and
                    settle the credit with the buyer directly.
                  </HelperText>
                </FormGroup>
                <FieldGrid>
                  <FormGroup>
                    <Label>Support email</Label>
                    <Input type="email" value={editForm.support_email} onChange={(e) => setEditForm({ ...editForm, support_email: e.target.value })} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Website</Label>
                    <Input type="url" value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} placeholder="https://" />
                  </FormGroup>
                </FieldGrid>
                {userIsSuperAdmin && (
                  <FormGroup>
                    <Checkbox
                      checked={editForm.active}
                      onChange={() => setEditForm({ ...editForm, active: !editForm.active })}
                      label="Active — its boards can be listed and priced"
                    />
                  </FormGroup>
                )}
                <FormGroup>
                  <Label>Product overview</Label>
                  <Input value={editForm.tagline} onChange={(e) => setEditForm({ ...editForm, tagline: e.target.value })} placeholder="Cellular smart locks for every door." maxLength={200} />
                  <HelperText>One sentence about what you make. Shown under your name on the public Hardware providers page and the catalog.</HelperText>
                </FormGroup>
                <FormGroup>
                  <Label>Public about</Label>
                  <TextArea rows={4} value={editForm.about} onChange={(e) => setEditForm({ ...editForm, about: e.target.value })} placeholder="Who you are, shown on the public catalog. Blank lines start new paragraphs." />
                </FormGroup>
                <FormGroup>
                  <Label>Internal notes</Label>
                  <TextArea rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" type="button" onClick={() => setEditing(false)}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </ModalFooter>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </PageContainer>
  );
}

export default HardwareProviderDetail;
